import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for parsing Excel files using a Web Worker (SheetJS).
 * The worker runs off the main thread so the UI stays responsive
 * and the progress bar can animate freely during parsing.
 */
export function useExcelParser() {
  const [progress, setProgress] = useState({ stage: '', percent: 0, message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const creepRef = useRef(null);
  const workerRef = useRef(null);

  const startCreep = useCallback((from, cap, intervalMs = 50) => {
    if (creepRef.current) clearInterval(creepRef.current);
    let current = from;
    creepRef.current = setInterval(() => {
      const remaining = cap - current;
      const step = Math.max(1.0, remaining * 0.15);
      current = Math.min(cap - 0.1, current + step);
      setProgress(prev => ({ ...prev, percent: Math.round(current * 10) / 10 }));
    }, intervalMs);
  }, []);

  const stopCreep = useCallback(() => {
    if (creepRef.current) { clearInterval(creepRef.current); creepRef.current = null; }
  }, []);

  useEffect(() => () => { stopCreep(); workerRef.current?.terminate(); }, [stopCreep]);

  const parseFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      setIsLoading(true);
      setError(null);
      setProgress({ stage: 'loading', percent: 2, message: `Loading ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)...` });
      startCreep(2, 20);

      const reader = new FileReader();

      reader.onerror = () => {
        stopCreep();
        const msg = 'Failed to read file';
        console.error('[ExcelParser]', msg);
        setError(msg);
        setIsLoading(false);
        reject(new Error(msg));
      };

      reader.onload = (event) => {
        stopCreep();
        setProgress({ stage: 'parsing', percent: 25, message: 'Parsing workbook...' });
        startCreep(25, 50); // creep while worker spins up

        const arrayBuffer = event.target.result;

        // Spawn worker from /public so it loads as a plain script (no ESM issues).
        // Strip trailing slash from BASE_URL to avoid double-slash when BASE_URL is '/'.
        const baseUrl = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
        const worker = new Worker(`${baseUrl}/excelParser.worker.js`);
        workerRef.current = worker;

        worker.onmessage = (msg) => {
          const { type, data, error: workerError, percent, message } = msg.data;

          if (type === 'progress') {
            stopCreep();
            setProgress({ stage: 'parsing', percent, message });
            // Creep between real progress updates
            startCreep(percent, Math.min(percent + 10, 95));
          } else if (type === 'result') {
            stopCreep();
            setProgress({ stage: 'complete', percent: 100, message: 'Parsing complete' });
            setIsLoading(false);
            worker.terminate();
            workerRef.current = null;
            console.log('[ExcelParser] Done. Sheets:', Object.keys(data).join(', '));
            resolve(data);
          } else if (type === 'error') {
            stopCreep();
            console.error('[ExcelParser] Worker error:', workerError);
            setError(workerError);
            setIsLoading(false);
            worker.terminate();
            workerRef.current = null;
            reject(new Error(workerError));
          }
        };

        worker.onerror = (err) => {
          stopCreep();
          const msg = err.message || 'Worker failed';
          console.error('[ExcelParser] Worker onerror:', msg);
          setError(msg);
          setIsLoading(false);
          worker.terminate();
          workerRef.current = null;
          reject(new Error(msg));
        };

        // Transfer the buffer to the worker (zero-copy)
        worker.postMessage({ arrayBuffer }, [arrayBuffer]);
      };

      reader.readAsArrayBuffer(file);
    });
  }, [startCreep, stopCreep]);

  const cancel = useCallback(() => {
    stopCreep();
    workerRef.current?.terminate();
    workerRef.current = null;
    setIsLoading(false);
    setProgress({ stage: '', percent: 0, message: '' });
  }, [stopCreep]);

  return { parseFile, progress, setProgress, isLoading, error, cancel, startCreep, stopCreep };
}
